import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, Moon, ShieldCheck, Sun } from "lucide-react";
import { api, formatApiError } from "../api";
import { KAIROO_AUTH } from "../constants/testIds";
import { useTheme } from "../contexts/ThemeContext";
import { fadeIn } from "../lib/motion";
import { KairooAttribution } from "../components/common/KairooAttribution";

// UX-CP-01 SCR-P08-00: a DISTINCT surface from the gym `/login` — same Scarlet visual system
// (UI_STANDARDS.md is global, not gym-only) but Kairoo wordmark/mark + copy, and its own guard so
// a gym Super Admin's credentials never land inside the Control Plane (routeAfterAuth below).
export default function KairooLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPending, setMfaPending] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const reducedMotion = useReducedMotion();

  const routeAfterAuth = async () => {
    const { data: me } = await api.get("/me");
    if (me.user_type !== "kairoo") {
      await api.post("/auth/logout");
      setMfaPending(false);
      setError("This portal is for Kairoo platform operators only. Gym staff should use the gym login page.");
      return;
    }
    navigate("/kairoo/dashboard");
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) setMfaPending(true);
      else await routeAfterAuth();
    } catch (e2) {
      setError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code: mfaCode });
      await routeAfterAuth();
    } catch (e2) {
      setError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scarlet-canvas min-h-screen flex items-center justify-center p-6 relative">
      <button
        onClick={toggleTheme}
        aria-label="Toggle day/night theme"
        className="scarlet-focusable absolute top-6 right-6 p-2.5 rounded-full glass-surface"
      >
        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <motion.div
        initial={reducedMotion ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={reducedMotion ? { duration: 0 } : { duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        className="glass-surface rounded-[14px] p-8 w-full max-w-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white shrink-0" style={{ background: "#1F2430" }}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <p className="font-bold text-lg leading-tight" style={{ color: "var(--scarlet-text)" }}>Kairoo</p>
            <p className="eyebrow leading-tight">Platform Control Center</p>
          </div>
        </div>

        {!mfaPending && (
          <form onSubmit={submitLogin} className="space-y-4">
            <Field label="Operator email" testId={KAIROO_AUTH.emailInput} value={email} onChange={setEmail} type="email" />
            <Field label="Password" testId={KAIROO_AUTH.passwordInput} value={password} onChange={setPassword} type="password" />
            <SubmitButton testId={KAIROO_AUTH.loginSubmit} busy={busy} label="Sign in to Control Plane" />
          </form>
        )}

        {mfaPending && (
          <form onSubmit={submitMfa} className="space-y-4">
            <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>MFA is mandatory for every Kairoo session. Enter the code we sent you.</p>
            <Field label="MFA code" testId={KAIROO_AUTH.mfaCodeInput} value={mfaCode} onChange={setMfaCode} />
            <SubmitButton testId={KAIROO_AUTH.mfaSubmit} busy={busy} label="Verify" />
          </form>
        )}

        {error && (
          <motion.p {...fadeIn(reducedMotion)} data-testid={KAIROO_AUTH.loginError} className="text-sm font-medium mt-4" style={{ color: "var(--scarlet-neg)" }}>
            {error}
          </motion.p>
        )}

        <KairooAttribution className="mt-6 pt-4" />
      </motion.div>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="eyebrow">{label}</label>
      <input
        data-testid={testId}
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="scarlet-focusable w-full px-3 py-2.5 rounded-[10px] text-sm outline-none"
        style={{ border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)" }}
      />
    </div>
  );
}

function SubmitButton({ testId, busy, label }) {
  return (
    <button
      type="submit"
      data-testid={testId}
      disabled={busy}
      className="scarlet-focusable w-full py-2.5 rounded-[12px] text-sm font-semibold text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
      style={{ background: "#1F2430" }}
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : label}
    </button>
  );
}
